import React from 'react';
import Sketch from 'react-p5';
import p5Types from 'p5';

interface Node {
  x: number;
  y: number;
  size: number;
  color: number[];
  connections: number[];
}

// Type for the p5 instance that react-p5 provides
type P5 = any;

const FractalGraphLogo: React.FC = () => {
  // Scale factor for zooming
  let scale = 1;
  // Target scale for smooth transitions
  let targetScale = 1;
  // Position of view center
  let offsetX = 0;
  let offsetY = 0;
  // Color phase for trippy effects
  let colorPhase = 0;
  // Fractal iteration level
  let fractalLevel = 0;
  // Array to store nodes at each level
  let nodes: Node[][] = [];
  // Maximum nodes per level
  const maxNodesPerLevel = 30;
  // Zoom threshold to trigger level change
  const zoomThreshold = 0.1;
  // Ready to draw flag
  let isReady = false;

  // Store reference to the canvas parent element
  let canvasParent: Element | null = null;

  const setup = (p5: P5, canvasParentRef: Element) => {
    canvasParent = canvasParentRef; // Store the parent reference
    
    // Set pixel density explicitly to 1 to avoid scaling issues
    p5.pixelDensity(1);
    
    // Initial setup uses window dimensions
    const canvas = p5.createCanvas(window.innerWidth, window.innerHeight).parent(canvasParentRef);
    
    // Apply direct styling to the canvas element to ensure it's centered
    const canvasElement = canvas.elt;
    if (canvasElement) {
      canvasElement.style.position = 'absolute';
      canvasElement.style.left = '50%';
      canvasElement.style.top = '50%';
      canvasElement.style.transform = 'translate(-50%, -50%)';
      canvasElement.style.display = 'block';
    }
    
    p5.colorMode(p5.HSB, 100);
    p5.frameRate(60);
    
    // Initialize the first level of nodes
    initNodes(p5, 0);
    
    // Small delay before starting to render to ensure container is ready
    setTimeout(() => {
      isReady = true;
    }, 100);
  };

  const initNodes = (p5: P5, level: number) => {
    if (!nodes[level]) {
      nodes[level] = [];
      
      // Create nodes for this level
      const nodeCount = level === 0 ? maxNodesPerLevel : maxNodesPerLevel + 10 * level;
      
      for (let i = 0; i < nodeCount; i++) {
        const radius = p5.random(100, 300) * (level + 1);
        const angle = p5.random(p5.TWO_PI);
        const node: Node = {
          x: p5.cos(angle) * radius,
          y: p5.sin(angle) * radius,
          size: p5.random(5, 15) / (level * 0.5 + 1),
          color: [p5.random(100), 70 + p5.random(30), 80 + p5.random(20)],
          connections: []
        };
        
        // Connect to some random nodes
        const connectionCount = p5.floor(p5.random(1, 5));
        for (let j = 0; j < connectionCount; j++) {
          const connectTo = p5.floor(p5.random(nodeCount));
          if (connectTo !== i) {
            node.connections.push(connectTo);
          }
        }
        
        nodes[level].push(node);
      }
    }
  };

  const draw = (p5: P5) => {
    // Don't render until ready
    if (!isReady) return;
    
    // Smooth zoom transition
    scale = p5.lerp(scale, targetScale, 0.1);
    
    // Clear background
    p5.background(0);
    
    // Reset all transformations at the start of each frame
    p5.resetMatrix();
    
    // Get actual canvas dimensions directly
    const centerX = p5.width / 2;
    const centerY = p5.height / 2;
    
    // Center and scale the view in a single transformation
    p5.translate(centerX + offsetX, centerY + offsetY);
    p5.scale(scale);
    
    // Update color phase for animation
    colorPhase = (colorPhase + 0.5) % 100;
    
    // Check if we need to change fractal level
    checkFractalLevel();
    
    // Draw current level and adjacent levels
    for (let l = Math.max(0, fractalLevel - 1); l <= fractalLevel + 1; l++) {
      if (l >= 0 && (l <= fractalLevel || scale <= zoomThreshold * 2)) {
        if (!nodes[l]) {
          initNodes(p5, l);
        }
        drawLevel(p5, l);
      }
    }
  };

  const drawLevel = (p5: P5, level: number) => {
    const currentNodes = nodes[level];
    
    // Calculate base alpha for the level based on distance from current level
    const baseLevelAlpha = level === fractalLevel ? 100 :
                         level < fractalLevel ? p5.map(scale, zoomThreshold, zoomThreshold * 2, 0, 100, true) :
                         p5.map(scale, zoomThreshold / 2, zoomThreshold, 100, 0, true);

    // If level alpha is negligible, skip drawing
    if (baseLevelAlpha <= 1) return;

    // Calculate an additional size-based and zoom-based alpha factor
    const zoomFadeAlpha = p5.map(scale, 0.1, 0.5, 0, 100, true);
    
    // Main level alpha calculated based on both factors
    const levelAlpha = p5.min(baseLevelAlpha, zoomFadeAlpha);

    // Draw connections first (edges)
    p5.strokeWeight(0.5 / (level * 0.2 + 1));
    for (let i = 0; i < currentNodes.length; i++) {
      const node = currentNodes[i];
      
      // Calculate node-specific alpha based on size (smaller nodes fade first when zooming out)
      const sizeFactor = p5.map(node.size, 5, 15, 0.5, 1, true); // Smaller nodes have smaller factor
      const nodeAlpha = levelAlpha * sizeFactor;
      
      // Skip drawing this node's connections if it would be nearly invisible
      if (nodeAlpha < 5) continue;
      
      for (const connIndex of node.connections) {
        if (connIndex < currentNodes.length) {
          const connNode = currentNodes[connIndex];
          const edgeHue = (node.color[0] + connNode.color[0]) / 2;
          
          // Apply node-specific alpha to edges
          p5.stroke(edgeHue, 60, 90, nodeAlpha * 0.4);
          p5.line(node.x, node.y, connNode.x, connNode.y);
        }
      }
    }

    // Draw nodes
    p5.noStroke();
    for (let i = 0; i < currentNodes.length; i++) {
      const node = currentNodes[i];
      
      // Calculate node-specific alpha again (as above)
      const sizeFactor = p5.map(node.size, 5, 15, 0.5, 1, true);
      const nodeAlpha = levelAlpha * sizeFactor;
      
      // Skip drawing nearly invisible nodes
      if (nodeAlpha < 5) continue;
      
      // Shift color based on phase for trippy effect
      const shiftedHue = (node.color[0] + colorPhase) % 100;
      
      // Apply node-specific alpha
      p5.fill(shiftedHue, node.color[1], node.color[2], nodeAlpha);
      p5.ellipse(node.x, node.y, node.size, node.size);
    }
  };

  const checkFractalLevel = () => {
    // If zoomed out enough, shift to the next level out
    if (scale < zoomThreshold && fractalLevel < 10) {
      fractalLevel++;
      targetScale = 1;
      offsetX = 0;
      offsetY = 0;
    }
    // If zoomed in enough, shift to the next level in
    else if (scale > 2 && fractalLevel > 0) {
      fractalLevel--;
      targetScale = 0.2;
      offsetX = 0;
      offsetY = 0;
    }
  };

  const mouseWheel = (p5: P5, event: any) => {
    // Adjust zoom based on mouse wheel
    targetScale *= event.delta > 0 ? 0.9 : 1.1;
    
    // Constrain zoom
    targetScale = p5.constrain(targetScale, 0.05, 3);
    
    // Prevent default behavior (page scrolling)
    return false;
  };

  const windowResized = (p5: P5) => {
    // Always use window dimensions for consistency
    p5.resizeCanvas(window.innerWidth, window.innerHeight);
    
    // Re-apply canvas styling after resize
    const canvasElement = p5.canvas.elt;
    if (canvasElement) {
      canvasElement.style.position = 'absolute';
      canvasElement.style.left = '50%';
      canvasElement.style.top = '50%';
      canvasElement.style.transform = 'translate(-50%, -50%)';
      canvasElement.style.display = 'block';
    }
  };

  return <Sketch setup={setup} draw={draw} mouseWheel={mouseWheel} windowResized={windowResized} />;
};

export default FractalGraphLogo;